import React, { useState, useMemo, useCallback } from "react";
import { createWorkbookFromTable } from "../../utils/table_export";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { observer } from "mobx-react-lite";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Box,
  HStack,
  useToast,
  Link,
  Collapse
} from "@chakra-ui/react";
import { useStores } from "../../store/store_context";
import SelfFilters from "./self_filters";

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

const TableData = observer(({ data, renderButtons }) => {
  const toast = useToast();
  const { pageStore } = useStores();
  const [sortConfig, setSortConfig] = useState({});

  const cleanNumber = (value) => {
    if (typeof value === "string") {
      const cleaned = value.replace(/[^\d,.-]/g, "").replace(",", ".");
      return parseFloat(cleaned) || 0;
    }
    return parseFloat(value) || 0;
  };

  const calculateTotalProfit = (item) => {
    const RevRub = cleanNumber(item.RevRub);
    const PaidRub = cleanNumber(item.PaidRub);
    const profit = cleanNumber(item.profit);
    const UF_CRM_1756460743245 = cleanNumber(item.UF_CRM_1756460743245);

    const openProfitValue = UF_CRM_1756460743245 || 0;

    const profitabilityFact = profit && RevRub ? (profit / RevRub) * 100 : 0;
    const profitabilityOpen =
      openProfitValue && RevRub ? (openProfitValue / RevRub) * 100 : 0;
    const profitInCash = profit
      ? PaidRub * (profitabilityFact / 100)
      : (openProfitValue / RevRub) * PaidRub
    let openProfit =
      openProfitValue && !item.ClosDate
        ? openProfitValue - profitInCash
        : 0;
    let totalProfit = openProfit + profitInCash;

    if (item.ClosDate && profit == 0) {
      openProfit = openProfitValue;
      totalProfit = openProfitValue;
    }
    return totalProfit
  }


  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc"
        };
      }
      return { key, direction: "desc" };
    });
  }, []);

  const formatCellValue = useCallback((value, isNumeric = false) => {
    if (value == null) return 0;

    if (typeof value === "number" && isNumeric) {
      return Math.round(value).toLocaleString("ru-RU");
    }

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-");
      return `${day}.${month}.${year}`;
    }

    return value || 0;
  }, []);

  const formatDateKey = useCallback((key) => {
    if (key === "Все периоды") return key;

    if (pageStore.year_or_mouth_view_mops === "month") {
      const [year, month] = key.split("-");
      return `${MONTH_NAMES[parseInt(month) - 1]} ${year}`;
    }

    return `${key}`;
  }, [pageStore.year_or_mouth_view_mops]);

  const { groupedData, allTimeKeys, totalSums } = useMemo(() => {

    if (!pageStore.year_or_mouth_view_mops) {
      return { groupedData: null, allTimeKeys: [], totalSums: {}, totalGlobalSum: 0 };
    }

    const getPrimaryKey = (item) => {
      return item.Manager
    };

    const grouped = {};
    const timeKeysSet = new Set();
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1–12

    const startDateRaw = pageStore.start_date_day || pageStore.start_date_month || pageStore.start_date_year;
    const endDateRaw = pageStore.end_date_day || pageStore.end_date_month || pageStore.end_date_year;

    const startDate = startDateRaw
      ? new Date(startDateRaw)
      : new Date(2020, 0, 1); // 2020-01-01 (можешь поменять)

    const endDate = endDateRaw
      ? new Date(endDateRaw)
      : new Date(currentYear, currentMonth - 1, 1);

    function getYearsInRange() {
      const years = [];
      for (let y = startDate.getFullYear(); y <= endDate.getFullYear(); y++) {
        years.push(y);
      }
      return years;
    }

    function getMonthsForYear(year) {
      let start = 1;
      let end = 12;

      if (year === startDate.getFullYear()) {
        start = startDate.getMonth() + 1;
      }

      if (year === endDate.getFullYear()) {
        end = endDate.getMonth() + 1;
      }

      const months = [];
      for (let m = start; m <= end; m++) {
        months.push(String(m).padStart(2, "0"));
      }

      return months;
    }

    if (pageStore.year_or_mouth_view_mops === "year") {
      const years = getYearsInRange();

      years.forEach(year => {
        timeKeysSet.add(String(year));
      });
    }

    // ✅ МЕСЯЧНЫЙ ВИД
    if (pageStore.year_or_mouth_view_mops === "month") {
      const years = getYearsInRange();

      years.forEach(year => {
        const months = getMonthsForYear(year);

        months.forEach(month => {
          const title = `${year}-${month.padStart(2, "0")}`;
          timeKeysSet.add(title);
        });
      });
    }

    data.forEach(item => {

      if (pageStore.workers && !item.active) return

      const primaryKey = getPrimaryKey(item);
      const date = new Date(new Date(item.Date).getTime() - 3 * 60 * 60 * 1000);

      let timeKey = "Все периоды";
      if (item.Date === null) {
        timeKey = null
      } else if (pageStore.year_or_mouth_view_mops === "month") {
        timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      } else if (pageStore.year_or_mouth_view_mops === "year") {
        timeKey = `${item.Date.split("-")[0]}`;
      }

      if (timeKey) timeKeysSet.add(timeKey);

      if (!grouped[primaryKey]) {
        grouped[primaryKey] = {
          timeGroups: {},
          totalRev: 0,
          totalProfit: 0,
          totalPays: 0
        };
      }
      grouped[primaryKey].timeGroups[timeKey] = grouped[primaryKey].timeGroups[timeKey] || { rev: 0, profit: 0, pays: 0 }

      if (item.DocType === "openRev") {
        grouped[primaryKey].timeGroups[timeKey].rev += item.RevRub
        grouped[primaryKey].totalRev += item.RevRub
      } else if (item.DocType === "openProfit") {
        grouped[primaryKey].timeGroups[timeKey].profit += calculateTotalProfit(item)
        grouped[primaryKey].totalProfit += calculateTotalProfit(item)
      } else if (item.DocType === "pays") {
        grouped[primaryKey].timeGroups[timeKey].pays += item.Sum
        grouped[primaryKey].totalPays += item.Sum
      }
    });

    function sortDateArray(arr) {
      return arr.sort((a, b) => {
        const isAInvalid = a.startsWith("Сделка не закрыта");
        const isBInvalid = b.startsWith("Сделка не закрыта");

        // ✅ "Сделка не закрыта" всегда внизу
        if (isAInvalid && !isBInvalid) return 1;
        if (!isAInvalid && isBInvalid) return -1;
        if (isAInvalid && isBInvalid) return 0;

        // ✅ Парсим "2025-06_Июнь"
        const [aYM] = a.split("_");
        const [bYM] = b.split("_");

        const [aYear, aMonth] = aYM.split("-").map(Number);
        const [bYear, bMonth] = bYM.split("-").map(Number);

        // ✅ Сравниваем по дате
        return aYear !== bYear
          ? aYear - bYear
          : aMonth - bMonth;
      });
    }

    const allTimeKeys = sortDateArray(
      Array.from(
        new Set(
          Array.from(timeKeysSet)
            .filter(k => k !== "Все периоды")
            .sort()
        )
      )
    );



    const groupedArray = Object.entries(grouped).map(([primaryKey, group]) => {

      return {
        primaryKey,
        timeGroups: group.timeGroups,
        totalRev: group.totalRev,
        totalProfit: group.totalProfit,
        totalPays: group.totalPays
      };
    });

    const totalSums = { totalRev: 0, totalProfit: 0, totalPays: 0 };

    groupedArray.forEach(group => {
      Object.entries(group.timeGroups).forEach(([timeKey, item]) => {
        if (!totalSums[timeKey]) {
          totalSums[timeKey] = { rev: 0, profit: 0, pays: 0 };
        }
        totalSums[timeKey].rev += item.rev
        totalSums[timeKey].profit += item.profit
        totalSums[timeKey].pays += item.pays

        totalSums['totalRev'] += item.rev
        totalSums['totalProfit'] += item.profit
        totalSums['totalPays'] += item.pays
      });
    });




    return {
      groupedData: groupedArray,
      allTimeKeys,
      totalSums,
    };
  }, [data, pageStore.selected_view, pageStore.year_or_mouth_view_mops, pageStore.workers]);

  const getSortValue = useCallback((item, key) => {
    if (!key) return
    if (key === "manager") {
      return item.primaryKey
    }
    if (key.startsWith('period||')) {
      const periodKey = key.split('||');
      const prevKey = allTimeKeys[allTimeKeys.indexOf(periodKey[3]) - 1]
      const prev = item.timeGroups[prevKey]
      if (periodKey[1] == "rev") {
        if (periodKey[2] == "abs") return item.timeGroups[periodKey[3]]?.rev || 0
        if (periodKey[2] == "percent") return prev ? ((item.timeGroups[periodKey[3]]?.rev - prev?.rev) / prev?.rev) * 100 : 0;
        if (periodKey[2] == "part") return item.timeGroups[periodKey[3]]?.rev / totalSums[periodKey[3]].rev * 100 || 0
      }
      if (periodKey[1] == "profit") {
        if (periodKey[2] == "abs") return item.timeGroups[periodKey[3]]?.profit || 0
        if (periodKey[2] == "percent") return prev && prev?.profit ? Number(((item.timeGroups[periodKey[3]]?.profit - prev?.profit) / prev?.profit) * 100) : 0;
        if (periodKey[2] == "part") return item.timeGroups[periodKey[3]]?.profit / totalSums[periodKey[3]].profit * 100 || 0
      }
      if (periodKey[1] == "pays") {
        if (periodKey[2] == "abs") return item.timeGroups[periodKey[3]]?.pays || 0
        if (periodKey[2] == "percent") return prev ? ((item.timeGroups[periodKey[3]]?.pays - prev?.pays) / prev?.pays) * 100 : 0;
        if (periodKey[2] == "part") return item.timeGroups[periodKey[3]]?.pays / totalSums[periodKey[3]].pays * 100 || 0
      }
    }
    if (key.startsWith("total||")) {
      const totalKey = key.split("||")[1]
      if (totalKey == "rev") return item.totalRev
      if (totalKey == "profit") return item.totalProfit
      if (totalKey == "pays") return item.totalPays
    }
  }, [allTimeKeys.length, pageStore.year_or_mouth_view_mops]);

  const sortedGroupedData = useMemo(() => {
    if (!groupedData) return null;

    const compareItems = (a, b, key) => {
      const valueA = getSortValue(a, key);
      const valueB = getSortValue(b, key);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
      }

      return sortConfig.direction === 'asc'
        ? String(valueB).localeCompare(String(valueA))
        : String(valueA).localeCompare(String(valueB));
    };

    const sortedGroups = [...groupedData].sort((a, b) =>
      compareItems(a, b, sortConfig.key));

    return sortedGroups.map(group => ({
      ...group,
    }));
  }, [groupedData, sortConfig, getSortValue]);

  const handleExportToXLSX = useCallback(async () => {
    var can = await pageStore.CheckPermission("export excel")
    console.log("export:")
    console.log(can)
    if (!can) {
      toast({
        title: "У вас нет права экспорта в excel",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return
    }
    if (!groupedData) return;

    const table = document.querySelector("table");
    if (!table) return;

    const workbook = createWorkbookFromTable(table);

    // Сохраняем файл
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "table.xlsx");
  }, [groupedData, allTimeKeys, formatCellValue, formatDateKey]);

  const lastType =
    pageStore.part_column_mops
      ? "part"
      : pageStore.percent_column_mops
        ? "percent"
        : pageStore.absolute_column_mops
          ? "abs"
          : null;

  const isMonthDivider = (metric, type) =>
    metric === "pays" && type === lastType;

  const renderColumns = () =>

    pageStore.year_or_mouth_view_mops &&
    allTimeKeys.map(timeKey => (
      <React.Fragment key={timeKey}>
        {(
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
            {formatDateKey(timeKey)}
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
          </Th>
        )}

        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
          </Th>
        )}

        {pageStore.absolute_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
          </Th>
        )}

        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
          >
          </Th>
        )}

        {pageStore.absolute_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            boxShadow={
              isMonthDivider("pays", "abs")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            boxShadow={
              isMonthDivider("pays", "percent")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
          </Th>
        )}

        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            boxShadow={
              isMonthDivider("pays", "part")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
          </Th>

        )}
      </React.Fragment>
    ));

  const renderColumnsTwo = () =>
    pageStore.year_or_mouth_view_mops &&
    allTimeKeys.map(timeKey => (
      <React.Fragment key={timeKey}>
        {pageStore.absolute_column_mops && (
          <Th
            color="white"
            textAlign="right"
            onClick={() => handleSort(`period||rev||abs||${timeKey}`)}
            cursor="pointer"
          >
            Откр. выручка
            {sortConfig.key === `period||rev||abs||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||rev||percent||${timeKey}`)}
          >
            {pageStore.absolute_column_mops ? "" : "Откр. выручка"} %
            {sortConfig.key === `period||rev||percent||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}


        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||rev||part||${timeKey}`)}
          >
            Доля, %
            {sortConfig.key === `period||rev||part||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.absolute_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||profit||abs||${timeKey}`)}
          >
            Откр. прибыль
            {sortConfig.key === `period||profit||abs||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||profit||percent||${timeKey}`)}
          >
            {pageStore.absolute_column_mops ? "" : "Откр. прибыль"} %
            {sortConfig.key === `period||profit||percent||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||profit||part||${timeKey}`)}
          >
            Доля, %
            {sortConfig.key === `period||profit||part||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.absolute_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||pays||abs||${timeKey}`)}
            boxShadow={
              isMonthDivider("pays", "abs")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
            Оплаты
            {sortConfig.key === `period||pays||abs||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.percent_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||pays||percent||${timeKey}`)}
            boxShadow={
              isMonthDivider("pays", "percent")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
            {pageStore.absolute_column_mops ? "" : "Оплаты"} %
            {sortConfig.key === `period||pays||percent||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}

        {pageStore.part_column_mops && (
          <Th
            color="white"
            textAlign="right"
            cursor="pointer"
            onClick={() => handleSort(`period||pays||part||${timeKey}`)}
            boxShadow={
              isMonthDivider("pays", "part")
                ? "inset -2px 0 0 #CBD5E0"
                : undefined
            }
          >
            Доля, %
            {sortConfig.key === `period||pays||part||${timeKey}` && (
              <Box as="span" ml={2}>
                {sortConfig.direction === "asc" ? "↑" : "↓"}
              </Box>
            )}
          </Th>
        )}
      </React.Fragment>
    ));


  const renderTableHeader = () => (
    <Thead bgColor={"black"} position={"sticky"} top={"0px"} zIndex={11}>
      <Tr>
        <Th />
        {renderColumns()}
        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
        >
          Итого
        </Th>

        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
        ></Th>

        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
        ></Th>
      </Tr>
      <Tr>
        <Th
          color="white"
          onClick={() => handleSort('manager')}
          cursor="pointer"
        >
          ФИО
          {sortConfig.key === 'manager' && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
        {renderColumnsTwo()}
        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
          onClick={() => handleSort(`total||rev`)}
        >
          Итого Откр. Выручка
          {sortConfig.key === `total||rev` && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>

        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
          onClick={() => handleSort(`total||profit`)}
        >
          Итого Откр. Прибыль
          {sortConfig.key === `total||profit` && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>

        <Th
          color="white"
          textAlign="right"
          cursor="pointer"
          onClick={() => handleSort(`total||pays`)}
        >
          Итого Оплаты
          {sortConfig.key === `total||pays` && (
            <Box as="span" ml={2}>
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </Box>
          )}
        </Th>
      </Tr>
    </Thead>
  );

  const renderPercentCell = (currentVal, prevVal, type = "rev") => {
    if (!prevVal || prevVal === 0) {
      return (
        <Td textAlign="right" fontWeight="bold" color="gray.400" boxShadow={
          isMonthDivider(type, "percent")
            ? "inset -2px 0 0 #CBD5E0"
            : undefined
        } py={pageStore.rowHeight}>
          —
        </Td>
      );
    }

    const percent = ((currentVal - prevVal) / prevVal) * 100;

    // Логика цвета
    let cellColor = "green.300"; // Салатовый (> 20)
    if (percent < 0) {
      cellColor = "red.500";     // Красный (упало)
    } else if (percent <= 20) {
      cellColor = "yellow.400";  // Желтый (0 - 20)
    }

    // Добавляем плюс для положительных значений
    const formattedValue = formatCellValue(percent, true);
    const displayValue = percent > 0 ? `+${formattedValue}%` : `${formattedValue}%`;

    return (
      <Td textAlign="right" fontWeight="bold" color={cellColor} boxShadow={
        isMonthDivider(type, "percent")
          ? "inset -2px 0 0 #CBD5E0"
          : undefined
      } py={pageStore.rowHeight}>
        {displayValue}
      </Td>
    );
  };

  const renderTotalRow = () => (
    <Tr bg="#555">
      <Td fontWeight="bold" color="white" py={pageStore.rowHeight}>Итого {sortedGroupedData.length}</Td>
      {pageStore.year_or_mouth_view_mops &&
        allTimeKeys.map((timeKey, i) => {
          const current = totalSums[timeKey] ?? 0;
          const prevKey = allTimeKeys[i - 1];
          const prev = prevKey ? totalSums[prevKey] ?? 0 : 0;
          return (
            <React.Fragment key={timeKey}>
              {/* --- ВЫРУЧКА --- */}
              {pageStore.absolute_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  {formatCellValue(current.rev, true)}
                </Td>
              )}

              {pageStore.percent_column_mops && renderPercentCell(current.rev, prev.rev)}

              {pageStore.part_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  100%
                </Td>
              )}

              {/* --- ПРИБЫЛЬ --- */}
              {pageStore.absolute_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  {formatCellValue(current.profit, true)}
                </Td>
              )}

              {pageStore.percent_column_mops && renderPercentCell(current.profit, prev.profit)}

              {pageStore.part_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                  100%
                </Td>
              )}

              {/* --- ОПЛАТЫ --- */}
              {pageStore.absolute_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" boxShadow={
                  isMonthDivider("pays", "abs")
                    ? "inset -2px 0 0 #CBD5E0"
                    : undefined
                } py={pageStore.rowHeight}>
                  {formatCellValue(current.pays, true)}
                </Td>
              )}

              {pageStore.percent_column_mops && renderPercentCell(current.pays, prev.pays, "pays")}

              {pageStore.part_column_mops && (
                <Td textAlign="right" fontWeight="bold" color="white" boxShadow={
                  isMonthDivider("pays", "part")
                    ? "inset -2px 0 0 #CBD5E0"
                    : undefined
                } py={pageStore.rowHeight}>
                  100%
                </Td>
              )}

            </React.Fragment>
          );
        })}
      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
        {formatCellValue(totalSums.totalRev, true)}
      </Td>

      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
        {formatCellValue(totalSums.totalProfit, true)}
      </Td>

      <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
        {formatCellValue(totalSums.totalPays, true)}
      </Td>
    </Tr>
  );

  const renderGroupRow = (group) => {

    return (
      <React.Fragment key={group.primaryKey}>
        <Tr bg="#333">
          <Td
            cursor="pointer"
            fontWeight="bold"
            color="white"
            py={pageStore.rowHeight}
          >
            {group.primaryKey}
          </Td>
          {pageStore.year_or_mouth_view_mops &&
            allTimeKeys.map((timeKey, i) => {
              const current = group.timeGroups[timeKey] ?? 0;
              const prevKey = allTimeKeys[i - 1];
              const prev = prevKey ? group.timeGroups[prevKey] ?? 0 : 0;

              return (
                <React.Fragment key={timeKey}>
                  {/* --- ВЫРУЧКА --- */}
                  {pageStore.absolute_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current.rev, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_mops && renderPercentCell(current.rev, prev.rev)}

                  {pageStore.part_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current.rev / totalSums[timeKey].rev * 100, true)}
                    </Td>
                  )}

                  {/* --- ПРИБЫЛЬ --- */}
                  {pageStore.absolute_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current.profit, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_mops && renderPercentCell(current.profit, prev.profit)}

                  {pageStore.part_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
                      {formatCellValue(current.profit / totalSums[timeKey].profit * 100, true)}
                    </Td>
                  )}

                  {/* --- ОПЛАТЫ --- */}
                  {pageStore.absolute_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" boxShadow={
                      isMonthDivider("pays", "abs")
                        ? "inset -2px 0 0 #CBD5E0"
                        : undefined
                    } py={pageStore.rowHeight}>
                      {formatCellValue(current.pays, true)}
                    </Td>
                  )}

                  {pageStore.percent_column_mops && renderPercentCell(current.pays, prev.pays, "pays")}

                  {pageStore.part_column_mops && (
                    <Td textAlign="right" fontWeight="bold" color="white" boxShadow={
                      isMonthDivider("pays", "part")
                        ? "inset -2px 0 0 #CBD5E0"
                        : undefined
                    } py={pageStore.rowHeight}>
                      {formatCellValue(current.pays / totalSums[timeKey].pays * 100, true)}
                    </Td>
                  )}

                </React.Fragment>
              );
            })}
          <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
            {formatCellValue(group.totalRev, true)}
          </Td>

          <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
            {formatCellValue(group.totalProfit, true)}
          </Td>

          <Td textAlign="right" fontWeight="bold" color="white" py={pageStore.rowHeight}>
            {formatCellValue(group.totalPays, true)}
          </Td>
        </Tr>
      </React.Fragment>
    );
  };

  const buttons = (
    <>
      <Button onClick={handleExportToXLSX} bg="#333" color="white" width="15vw" _hover={{ bg: "#444" }}>
        Экспорт в Excel
      </Button>
    </>
  )


  return (
    <>
      <Collapse in={pageStore.open_filter} animateOpacity>
        {renderButtons ? renderButtons(buttons) : buttons}
      </Collapse>
      {pageStore?.data_mops.length ?
        <Box w="100%">
          <SelfFilters/>
          <Box maxHeight="95vh" overflowX="auto" borderRadius="md" borderWidth={1} borderColor="gray.300">
            <Table tableLayout="auto" variant="simple" bg="" color="white" width="auto" maxWidth="fit-content">
              {renderTableHeader()}
              <Tbody>
                {renderTotalRow()}
                {sortedGroupedData?.map(renderGroupRow)}
              </Tbody>
            </Table>
          </Box>
        </Box>
        : null}
    </>
  );
});

export default TableData;